import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { Box, Button, Card, CardContent, CardHeader, IconButton, Input, InputAdornment, Tooltip, Dialog, DialogActions, DialogTitle, FormGroup, FormControlLabel, Switch} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import ApiService from "../../services/categoryManagementApi";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { COMMISSION_OPTIONS } from "../../utils/constants";
import { Loader } from "../../components/customLoader";
import { Helmet } from "react-helmet-async";
import View from "./View";
import Swal from "sweetalert2";
import { capitalize } from "../../utils/helpers";

const { dealerCommissionApi } = ApiService;

export default function List() {
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false); 

  const confirmStatusChange = (id, currentStatus, newStatus) => {
    if (currentStatus === newStatus) return;

    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to change the status to "${capitalize(newStatus)}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it!",
    }).then((result) => {
      if (result.isConfirmed) {
        handleStatusChange(id, newStatus);
      }
    });
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const payload = { id, status: newStatus };
      const response = await dealerCommissionApi.updateDealerCommission(payload);
      if (response.status === 200) {
        toast.success(response.data.message);
        setRefresh(!refresh); 
      } else {
        toast.error("Failed to update payment status.");
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status || [],
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false); 
      const res = await dealerCommissionApi.getAllDealerCommission(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
        }
        setLoaderDelay(true); 
      }, 500);
    } catch (err) {
        setData(err?.response?.data?.message?.data);
        errorHandler(err);
        setLoaderDelay(false); 
    } finally {
        setIsLoading(false);
        setLoaderDelay(false); 
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Order ID",
      sortable: true,
      selector: (row) => row?.orderNumber,
    },
    {
      name: "Dealer Name",
      sortable: true,
      selector: (row) => row?.dealerName,
    },
    {
      name: "Amount",
      sortable: true,
      selector: (row) => row.amount,
    },
    {
         name: "Commission Status",
         sortable: true,
         cell: (row) => (
           <select
             value={row.status}
             onChange={(e) => confirmStatusChange(row._id, row.status, e.target.value)}
             style={{
               padding: "5px",
               borderRadius: "5px",
               border: "1px solid #ccc",
               background: "#fff",
             }}
           >
             {COMMISSION_OPTIONS.map((status) => (
               <option key={status.value} value={status.value}>
                 {status.label}
               </option>
             ))}
           </select>
         ),
       },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                setModalShow(true);
                setViewId(row._id);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>          
        </>
      ),
    },
  ];

  return (
    <Card>
       <Helmet>
         <title> Dealer Commission</title>
       </Helmet>
      <CardHeader title="Dealer Commission Management" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TableFilter
          data={COMMISSION_OPTIONS}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />

        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  setSearchItem("");
                  fetchUsers("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
      </Box>
        <CardContent>
          <DataTable
            customStyles={{
              headCells: {
                style: {
                  fontSize: "15px",
                  fontWeight: "bold",
                },
              },
            }}
            columns={columns}
            data={data}
            pagination
            paginationServer
            paginationTotalRows={totalPages}
            onChangePage={handlePageChange}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={[5, 10, 20, 50]}
            onChangeRowsPerPage={(currentRowsPerPage) =>
              setPageSize(currentRowsPerPage)
            }
            progressPending={isLoading || !loaderDelay}
            progressComponent={<Loader />}
            className="custom"
          />
        </CardContent>
      {modalShow ? (
        <View
          show={modalShow}
          onHide={() => setModalShow(false)}
          id={viewId}
        />
      ) : (
        ""
      )}
    </Card>
  );
}